// src/hooks/useBusinessContracts.ts - Contrats clients et paiements (Super Admin)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import type { DocumentData, UpdateData } from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import {
  BusinessContract,
  BusinessContractPayment,
  BusinessContractStatus,
  CONTRACT_DURATION_MONTHS,
  getCoverageExpiryDate,
  getPendingOtherPaymentLabels,
  hasPaymentDelay,
  hasPendingOtherPayments,
  isPaymentDueWithinDays,
  mapContractFromFirestore,
  mapPaymentFromFirestore,
} from '../types/businessContract';
import type {
  BusinessContractDocument,
  BusinessContractPaymentDocument,
  ContractPaymentStatus,
  ContractPaymentType,
} from '../types/businessContract';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const canAccessAdmin = (isSuperAdmin: boolean, isCSM: boolean) => isSuperAdmin || isCSM;

function toContractData(data: DocumentData | undefined): Partial<BusinessContractDocument> & Pick<BusinessContractDocument, 'businessId'> {
  const d = data ?? {};
  return {
    businessId: String(d.businessId ?? ''),
    startDate: d.startDate,
    endDate: d.endDate,
    monthlyAmount: d.monthlyAmount,
    currency: d.currency,
    status: d.status,
    description: d.description,
    signedAt: d.signedAt,
    protocolAttachmentUrl: d.protocolAttachmentUrl,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
  };
}

function toPaymentData(data: DocumentData | undefined): Partial<BusinessContractPaymentDocument> & Pick<BusinessContractPaymentDocument, 'businessId'> {
  const d = data ?? {};
  return {
    businessId: String(d.businessId ?? ''),
    paymentDate: d.paymentDate,
    amount: d.amount,
    originalAmount: d.originalAmount,
    discountLabel: d.discountLabel,
    discountPercent: d.discountPercent,
    paymentType: d.paymentType ?? 'monthly',
    status: d.status ?? 'paid',
    coverageStartDate: d.coverageStartDate,
    coverageEndDate: d.coverageEndDate,
    feeLabel: d.feeLabel,
    reference: d.reference,
    notes: d.notes,
    attachmentUrl: d.attachmentUrl,
    createdAt: d.createdAt,
    createdBy: d.createdBy,
  };
}

/** Contrat actuel d'un business (le plus récent actif ou le dernier créé) */
export const useBusinessContract = (businessId: string | undefined) => {
  const { isSuperAdmin, isCSM, currentBusiness } = useAuth();
  const hasAccess = canAccessAdmin(isSuperAdmin ?? false, isCSM ?? false) || (!!currentBusiness && businessId === currentBusiness.id);

  return useQuery({
    queryKey: ['businessContract', businessId],
    queryFn: async (): Promise<BusinessContract | null> => {
      if (!businessId || !hasAccess) return null;
      const q = query(
        collection(db, COLLECTIONS.businessContracts),
        where('businessId', '==', businessId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q).catch((err) => {
        handleFirestoreError(err, 'useBusinessContract');
        throw err;
      });
      if (snapshot.empty) return null;
      const firstDoc = snapshot.docs[0];
      return mapContractFromFirestore(firstDoc.id, toContractData(firstDoc.data()));
    },
    enabled: !!businessId && hasAccess,
  });
};

/** Échéances / paiements d'un business, triés par création (puis date de règlement) */
export const useBusinessContractPayments = (businessId: string | undefined) => {
  const { isSuperAdmin, isCSM, currentBusiness } = useAuth();
  const hasAccess = canAccessAdmin(isSuperAdmin ?? false, isCSM ?? false) || (!!currentBusiness && businessId === currentBusiness.id);

  return useQuery({
    queryKey: ['businessContractPayments', businessId],
    queryFn: async (): Promise<BusinessContractPayment[]> => {
      if (!businessId || !hasAccess) return [];
      const q = query(
        collection(db, COLLECTIONS.businessContractPayments),
        where('businessId', '==', businessId)
      );
      const snapshot = await getDocs(q).catch((err) => {
        handleFirestoreError(err, 'useBusinessContractPayments');
        throw err;
      });
      return snapshot.docs
        .map((d) => mapPaymentFromFirestore(d.id, toPaymentData(d.data())))
        .sort((a, b) => {
          const ta = a.paymentDate?.getTime() ?? a.createdAt.getTime();
          const tb = b.paymentDate?.getTime() ?? b.createdAt.getTime();
          return tb - ta;
        });
    },
    enabled: !!businessId && hasAccess,
  });
};

export interface CreateContractInput {
  businessId: string;
  startDate: Date;
  endDate?: Date;
  monthlyAmount: number;
  currency?: string;
  description?: string;
  signedAt?: Date;
  protocolAttachmentUrl?: string;
}

export const useCreateBusinessContract = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();
  const hasAccess = canAccessAdmin(isSuperAdmin ?? false, isCSM ?? false);

  return useMutation({
    mutationFn: async (input: CreateContractInput): Promise<BusinessContract> => {
      if (!hasAccess) throw new Error('Accès non autorisé');
      const startDate = input.startDate instanceof Date ? input.startDate : new Date(input.startDate);
      const endDate = input.endDate instanceof Date
        ? input.endDate
        : dayjs(startDate).add(CONTRACT_DURATION_MONTHS, 'month').toDate();
      const now = new Date();
      const data = {
        businessId: input.businessId,
        startDate: Timestamp.fromDate(startDate),
        endDate: Timestamp.fromDate(endDate),
        monthlyAmount: input.monthlyAmount,
        currency: input.currency ?? 'FCFA',
        status: 'active',
        description: input.description ?? null,
        signedAt: input.signedAt ? Timestamp.fromDate(input.signedAt) : null,
        protocolAttachmentUrl: input.protocolAttachmentUrl ?? null,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      };
      const ref = await addDoc(collection(db, COLLECTIONS.businessContracts), data);
      const snap = await getDoc(doc(db, COLLECTIONS.businessContracts, ref.id));
      if (!snap.exists()) throw new Error('Document créé introuvable');
      return mapContractFromFirestore(snap.id, toContractData(snap.data()));
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['businessContract', vars.businessId] });
    },
  });
};

export interface UpdateContractInput {
  contractId: string;
  businessId: string;
  startDate?: Date;
  endDate?: Date;
  monthlyAmount?: number;
  status?: BusinessContractStatus;
  description?: string;
  signedAt?: Date;
  protocolAttachmentUrl?: string;
}

export const useUpdateBusinessContract = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();
  const hasAccess = canAccessAdmin(isSuperAdmin ?? false, isCSM ?? false);

  return useMutation({
    mutationFn: async (input: UpdateContractInput): Promise<void> => {
      if (!hasAccess) throw new Error('Accès non autorisé');
      const ref = doc(db, COLLECTIONS.businessContracts, input.contractId);
      const updateFields: Record<string, Timestamp | number | string | null | undefined> = {
        updatedAt: Timestamp.fromDate(new Date()),
      };
      if (input.startDate !== undefined) {
        const startDate = input.startDate instanceof Date ? input.startDate : new Date(input.startDate);
        updateFields.startDate = Timestamp.fromDate(startDate);
        if (input.endDate === undefined) {
          updateFields.endDate = Timestamp.fromDate(dayjs(startDate).add(CONTRACT_DURATION_MONTHS, 'month').toDate());
        }
      }
      if (input.endDate !== undefined) {
        const endDate = input.endDate instanceof Date ? input.endDate : new Date(input.endDate);
        updateFields.endDate = Timestamp.fromDate(endDate);
      }
      if (input.monthlyAmount !== undefined) updateFields.monthlyAmount = input.monthlyAmount;
      if (input.description !== undefined) updateFields.description = input.description || null;
      if (input.status !== undefined) updateFields.status = input.status;
      if (input.signedAt instanceof Date) updateFields.signedAt = Timestamp.fromDate(input.signedAt);
      if (input.protocolAttachmentUrl !== undefined) updateFields.protocolAttachmentUrl = input.protocolAttachmentUrl;
      await updateDoc(ref, updateFields);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['businessContract', vars.businessId] });
    },
  });
};

export interface RecordPaymentInput {
  businessId: string;
  /** Obligatoire si status = paid ; sinon omis */
  paymentDate?: Date | null;
  amount?: number;
  originalAmount?: number | null;
  discountLabel?: string | null;
  discountPercent?: number | null;
  paymentType: ContractPaymentType;
  status?: ContractPaymentStatus;
  coverageStartDate?: Date;
  coverageEndDate?: Date;
  feeLabel?: string;
  reference?: string;
  notes?: string;
  attachmentUrl?: string;
}

export const useRecordContractPayment = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM, userProfile } = useAuth();
  const hasAccess = canAccessAdmin(isSuperAdmin ?? false, isCSM ?? false);

  return useMutation({
    mutationFn: async (input: RecordPaymentInput): Promise<BusinessContractPayment> => {
      if (!hasAccess) throw new Error('Accès non autorisé');
      const now = new Date();
      const hasCoverage = input.paymentType === 'monthly' || input.paymentType === 'complimentary';
      const amount = input.paymentType === 'complimentary' ? 0 : (input.amount ?? 0);
      const status = input.paymentType === 'complimentary' ? 'paid' : (input.status ?? 'pending');
      const paymentDate =
        status === 'paid'
          ? (input.paymentDate instanceof Date
              ? input.paymentDate
              : input.paymentDate
                ? new Date(input.paymentDate)
                : now)
          : null;
      if (status === 'paid' && !paymentDate) {
        throw new Error('La date de paiement est obligatoire pour un règlement payé');
      }
      const data = {
        businessId: input.businessId,
        paymentDate: paymentDate ? Timestamp.fromDate(paymentDate) : null,
        amount,
        originalAmount:
          input.originalAmount != null && input.originalAmount > amount
            ? input.originalAmount
            : null,
        discountLabel: input.discountLabel ?? null,
        discountPercent:
          input.discountPercent != null && input.discountPercent > 0
            ? input.discountPercent
            : null,
        paymentType: input.paymentType,
        status,
        coverageStartDate: hasCoverage && input.coverageStartDate != null && input.coverageEndDate != null
          ? Timestamp.fromDate(input.coverageStartDate instanceof Date ? input.coverageStartDate : new Date(input.coverageStartDate))
          : null,
        coverageEndDate: hasCoverage && input.coverageStartDate != null && input.coverageEndDate != null
          ? Timestamp.fromDate(input.coverageEndDate instanceof Date ? input.coverageEndDate : new Date(input.coverageEndDate))
          : null,
        feeLabel: input.feeLabel ?? null,
        reference: input.reference ?? null,
        notes: input.notes ?? null,
        attachmentUrl: input.attachmentUrl ?? null,
        createdAt: Timestamp.fromDate(now),
        createdBy: userProfile?.id ?? null,
      };
      const ref = await addDoc(collection(db, COLLECTIONS.businessContractPayments), data);
      const snap = await getDoc(doc(db, COLLECTIONS.businessContractPayments, ref.id));
      if (!snap.exists()) throw new Error('Document créé introuvable');
      return mapPaymentFromFirestore(snap.id, toPaymentData(snap.data()));
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['businessContractPayments', vars.businessId] });
    },
  });
};

export const useDeleteContractPayment = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();
  const hasAccess = canAccessAdmin(isSuperAdmin ?? false, isCSM ?? false);

  return useMutation({
    mutationFn: async ({ paymentId, businessId }: { paymentId: string; businessId: string }): Promise<void> => {
      if (!hasAccess) throw new Error('Accès non autorisé');
      const ref = doc(db, COLLECTIONS.businessContractPayments, paymentId);
      await deleteDoc(ref);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['businessContractPayments', vars.businessId] });
      queryClient.invalidateQueries({ queryKey: ['allBusinessContracts'] });
    },
  });
};

export const useUpdateContractPaymentStatus = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();
  const hasAccess = canAccessAdmin(isSuperAdmin ?? false, isCSM ?? false);

  return useMutation({
    mutationFn: async (input: {
      paymentId: string;
      businessId: string;
      status: ContractPaymentStatus;
      paymentDate?: Date;
      attachmentUrl?: string;
    }): Promise<void> => {
      if (!hasAccess) throw new Error('Accès non autorisé');
      const ref = doc(db, COLLECTIONS.businessContractPayments, input.paymentId);
      const updateFields: UpdateData<DocumentData> = {
        status: input.status,
      };
      if (input.status === 'paid') {
        updateFields.paymentDate = Timestamp.fromDate(
          input.paymentDate instanceof Date ? input.paymentDate : new Date()
        );
      } else {
        updateFields.paymentDate = null;
      }
      if (input.attachmentUrl !== undefined) {
        updateFields.attachmentUrl = input.attachmentUrl || null;
      }
      await updateDoc(ref, updateFields);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['businessContractPayments', vars.businessId] });
      queryClient.invalidateQueries({ queryKey: ['allBusinessContracts'] });
    },
  });
};

export interface UpdateContractPaymentInput {
  paymentId: string;
  businessId: string;
  paymentDate?: Date | null;
  amount?: number;
  originalAmount?: number | null;
  discountLabel?: string | null;
  discountPercent?: number | null;
  paymentType: ContractPaymentType;
  status: ContractPaymentStatus;
  coverageStartDate?: Date | null;
  coverageEndDate?: Date | null;
  feeLabel?: string | null;
  reference?: string | null;
  notes?: string | null;
  attachmentUrl?: string | null;
}

/** Modifie une échéance / un paiement tant qu’il n’est pas encore marqué payé. */
export const useUpdateContractPayment = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();
  const hasAccess = canAccessAdmin(isSuperAdmin ?? false, isCSM ?? false);

  return useMutation({
    mutationFn: async (input: UpdateContractPaymentInput): Promise<BusinessContractPayment> => {
      if (!hasAccess) throw new Error('Accès non autorisé');
      const ref = doc(db, COLLECTIONS.businessContractPayments, input.paymentId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error('Paiement introuvable');

      const current = mapPaymentFromFirestore(snap.id, toPaymentData(snap.data()));
      if (current.status === 'paid') {
        throw new Error('Un paiement déjà payé ne peut pas être modifié');
      }
      if (current.businessId !== input.businessId) {
        throw new Error('Paiement non rattaché à cet établissement');
      }

      const hasCoverage = input.paymentType === 'monthly' || input.paymentType === 'complimentary';
      const amount = input.paymentType === 'complimentary' ? 0 : (input.amount ?? 0);
      const status = input.paymentType === 'complimentary' ? 'paid' : input.status;
      const paymentDate =
        status === 'paid'
          ? (input.paymentDate instanceof Date
              ? input.paymentDate
              : input.paymentDate
                ? new Date(input.paymentDate)
                : new Date())
          : null;
      if (status === 'paid' && !paymentDate) {
        throw new Error('La date de paiement est obligatoire pour un règlement payé');
      }

      const updateFields: UpdateData<DocumentData> = {
        paymentDate: paymentDate ? Timestamp.fromDate(paymentDate) : null,
        amount,
        originalAmount:
          input.originalAmount != null && input.originalAmount > amount
            ? input.originalAmount
            : null,
        discountLabel: input.discountLabel ?? null,
        discountPercent:
          input.discountPercent != null && input.discountPercent > 0
            ? input.discountPercent
            : null,
        paymentType: input.paymentType,
        status,
        feeLabel: input.feeLabel ?? null,
        reference: input.reference ?? null,
        notes: input.notes ?? null,
        attachmentUrl: input.attachmentUrl ?? null,
        coverageStartDate:
          hasCoverage && input.coverageStartDate != null && input.coverageEndDate != null
            ? Timestamp.fromDate(
                input.coverageStartDate instanceof Date
                  ? input.coverageStartDate
                  : new Date(input.coverageStartDate)
              )
            : null,
        coverageEndDate:
          hasCoverage && input.coverageStartDate != null && input.coverageEndDate != null
            ? Timestamp.fromDate(
                input.coverageEndDate instanceof Date
                  ? input.coverageEndDate
                  : new Date(input.coverageEndDate)
              )
            : null,
      };

      await updateDoc(ref, updateFields);
      const updated = await getDoc(ref);
      if (!updated.exists()) throw new Error('Paiement introuvable après mise à jour');
      return mapPaymentFromFirestore(updated.id, toPaymentData(updated.data()));
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['businessContractPayments', vars.businessId] });
      queryClient.invalidateQueries({ queryKey: ['allBusinessContracts'] });
    },
  });
};

/** Statut contrat/paiements pour l'espace client (lecture seule, business peut voir son propre contrat) */
export const useMyContractPaymentStatus = (businessId: string | undefined) => {
  const contractQuery = useBusinessContract(businessId);
  const paymentsQuery = useBusinessContractPayments(businessId);

  const contract = contractQuery.data ?? null;
  const payments = paymentsQuery.data ?? [];
  const isLoading = contractQuery.isLoading || paymentsQuery.isLoading;

  const coverageExpiry = getCoverageExpiryDate(payments);
  const paymentDelay = hasPaymentDelay(payments);
  const paymentDueSoon = isPaymentDueWithinDays(payments, 10);
  const pendingOtherPayments = hasPendingOtherPayments(payments);
  const pendingOtherPaymentLabels = getPendingOtherPaymentLabels(payments);

  return {
    contract,
    payments,
    coverageExpiry,
    paymentDelay,
    paymentDueSoon,
    pendingOtherPayments,
    pendingOtherPaymentLabels,
    isLoading,
    hasContract: !!contract,
  };
};

/** Tous les contrats avec leurs paiements pour la vue relances (Super Admin dashboard) */
export const useAllContractsWithPayments = () => {
  const { isSuperAdmin, isCSM } = useAuth();
  const hasAccess = canAccessAdmin(isSuperAdmin ?? false, isCSM ?? false);

  return useQuery({
    queryKey: ['allBusinessContracts'],
    queryFn: async (): Promise<Array<{
      contract: BusinessContract;
      payments: BusinessContractPayment[];
      coverageExpiry: Date | null;
    }>> => {
      if (!hasAccess) return [];
      const contractsSnap = await getDocs(
        query(collection(db, COLLECTIONS.businessContracts), orderBy('createdAt', 'desc'))
      );
      const contracts = contractsSnap.docs.map((d) =>
        mapContractFromFirestore(d.id, toContractData(d.data()))
      );

      const byBusiness = new Map<string, BusinessContract>();
      contracts.forEach((c) => {
        if (!byBusiness.has(c.businessId)) byBusiness.set(c.businessId, c);
      });

      const paymentsSnap = await getDocs(collection(db, COLLECTIONS.businessContractPayments));
      const allPayments = paymentsSnap.docs.map((d) =>
        mapPaymentFromFirestore(d.id, toPaymentData(d.data()))
      );

      const paymentsByBusiness = new Map<string, BusinessContractPayment[]>();
      allPayments.forEach((p) => {
        const list = paymentsByBusiness.get(p.businessId) ?? [];
        list.push(p);
        paymentsByBusiness.set(p.businessId, list);
      });

      return Array.from(byBusiness.entries()).map(([businessId, contract]) => {
        const payments = paymentsByBusiness.get(businessId) ?? [];
        const coverageExpiry = getCoverageExpiryDate(payments);
        return { contract, payments, coverageExpiry };
      });
    },
    enabled: hasAccess,
  });
};
